import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import styles from './QuizSignature.module.scss';
import cx from 'classnames';
import { useAppDispatch, useAppSelector } from '../../../store';
import { setTermsAnswer, setTermsIndex } from '../../../store/slices/quizSlice';
import { setTermsField } from '../../../store/slices/formDataSlice';
import { uploadBase64File } from '../../../api/reimbursement';
import { showToast } from '../../../store/slices/uiSlice';

// Icons
import EditIcon from '../../../assets/icons/edit.svg';

interface QuizSignatureProps {
  questionIndex: number;
  existingSignature?: string;
}

const QuizSignature: React.FC<QuizSignatureProps> = ({ questionIndex, existingSignature }) => {
  const dispatch = useAppDispatch();
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [isEditing, setIsEditing] = useState(!existingSignature);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [localDataUrl, setLocalDataUrl] = useState<string | null>(null);
  const [signatureBlobUrl, setSignatureBlobUrl] = useState<string | null>(null);
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);

  const reimbursementRef = useAppSelector((state) => state.payment.reimbursementRef);
  const signature = useAppSelector((state) => state.formData.termsAndConditions.signature);

  // Load image as base64 (for existing signatures from URL)
  const loadImageAsBase64 = useCallback(async (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  // Fetch signature as blob to bypass CORS issues
  useEffect(() => {
    const sigToLoad = existingSignature || signature;
    
    if (!sigToLoad) {
      setSignatureBlobUrl(null);
      return;
    }

    const isDataUrl = typeof sigToLoad === 'string' && sigToLoad.startsWith('data:');
    const isUrl = typeof sigToLoad === 'string' && /^https?:\/\//i.test(sigToLoad);

    // For data URLs, use directly
    if (isDataUrl) {
      setSignatureBlobUrl(sigToLoad);
      return;
    }

    // For remote URLs, fetch as blob
    if (isUrl) {
      let cancelled = false;
      setIsLoadingSignature(true);

      (async () => {
        try {
          const response = await fetch(sigToLoad);
          if (!response.ok) throw new Error('Failed to fetch signature');
          
          const blob = await response.blob();
          if (cancelled) return;
          
          const blobUrl = URL.createObjectURL(blob);
          setSignatureBlobUrl(blobUrl);
        } catch (err) {
          console.error('Failed to load signature as blob:', err);
          // Fallback to direct URL
          setSignatureBlobUrl(sigToLoad);
        } finally {
          if (!cancelled) {
            setIsLoadingSignature(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }
  }, [existingSignature, signature]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (signatureBlobUrl && signatureBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(signatureBlobUrl);
      }
    };
  }, [signatureBlobUrl]);

  // DPI-aware resize preserving strokes
  const resizeSignatureCanvas = useCallback(() => {
    const pad = sigCanvasRef.current;
    if (!pad) return;
    const canvas = pad.getCanvas();
    if (!canvas) return;

    // Save strokes data
    let data;
    try {
      data = pad.toData();
    } catch {
      data = null;
    }

    // Get wrapper width/height
    const wrapper = canvasWrapperRef.current;
    const w = wrapper ? wrapper.clientWidth : canvas.offsetWidth;
    const h = wrapper ? wrapper.clientHeight : canvas.offsetHeight || 150;

    // Set size with devicePixelRatio
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.round(w * ratio);
    canvas.height = Math.round(h * ratio);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    // Restore strokes
    if (data && data.length) {
      try {
        pad.fromData(data);
      } catch {
        // ignore restore errors
      }
    }
  }, []);

  // Initialize resize observer
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;

    if (wrapper && 'ResizeObserver' in window) {
      resizeObserverRef.current = new ResizeObserver(() => {
        resizeSignatureCanvas();
      });
      resizeObserverRef.current.observe(wrapper);
    }

    let t: NodeJS.Timeout;
    const onWinResize = () => {
      clearTimeout(t);
      t = setTimeout(() => resizeSignatureCanvas(), 120);
    };
    window.addEventListener('resize', onWinResize);

    // Initial resize
    setTimeout(resizeSignatureCanvas, 0);

    return () => {
      window.removeEventListener('resize', onWinResize);
      if (resizeObserverRef.current && wrapper) {
        resizeObserverRef.current.unobserve(wrapper);
      }
      resizeObserverRef.current = null;
    };
  }, [resizeSignatureCanvas]);

  // Enable/disable drawing based on isEditing
  useEffect(() => {
    const pad = sigCanvasRef.current;
    if (!pad) return;

    try {
      if (!isEditing && typeof pad.off === 'function') {
        pad.off();
      } else if (isEditing && typeof pad.on === 'function') {
        pad.on();
      }
    } catch {
      // ignore
    }
  }, [isEditing]);

  // Load existing signature
  useEffect(() => {
    const pad = sigCanvasRef.current;
    if (!pad) return;

    const sigToLoad = existingSignature || signature;

    if (!sigToLoad) {
      try {
        pad.clear();
      } catch {
        // ignore
      }
      return;
    }

    const isDataUrl = typeof sigToLoad === 'string' && sigToLoad.startsWith('data:');
    const isUrl = typeof sigToLoad === 'string' && /^https?:\/\//i.test(sigToLoad);

    let cancelled = false;

    (async () => {
      try {
        resizeSignatureCanvas();

        if (isDataUrl) {
          pad.fromDataURL(sigToLoad);
          setHasDrawn(true);
          return;
        }

        if (isUrl) {
          const base64 = await loadImageAsBase64(sigToLoad);
          if (cancelled) return;
          pad.fromDataURL(base64);
          setHasDrawn(true);
          return;
        }

        pad.fromDataURL(sigToLoad);
        setHasDrawn(true);
      } catch (err) {
        console.error('Signature load failed:', err);
        try {
          pad.clear();
        } catch {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [existingSignature, signature, loadImageAsBase64, resizeSignatureCanvas]);

  // Handle save signature
  const handleSaveSignature = async () => {
    const pad = sigCanvasRef.current;
    if (!pad || pad.isEmpty()) {
      dispatch(
        showToast({
          type: 'error',
          body: 'Bitte unterschreiben Sie zuerst.',
        })
      );
      return;
    }

    let dataURL: string;
    try {
      dataURL = pad.toDataURL('image/png');
    } catch (err) {
      console.error('toDataURL failed:', err);
      dispatch(
        showToast({
          type: 'error',
          body: 'Konnte Unterschrift nicht exportieren. Bitte neu unterschreiben.',
        })
      );
      return;
    }

    // Save the data URL locally so we can show it while the remote image loads
    setLocalDataUrl(dataURL);
    setIsUploading(true);
    setImageLoaded(false);

    try {
      const res = await uploadBase64File(
        { data: dataURL, documentType: 'RC_SIGNATURE' },
        reimbursementRef
      );

      // Update Redux state
      dispatch(setTermsAnswer({ index: questionIndex, answer: res.amazonUrl }));
      dispatch(setTermsField({ field: 'signature', value: res.amazonUrl }));
      dispatch(setTermsIndex(questionIndex + 1));

      setIsEditing(false);
      setHasDrawn(true);

      dispatch(
        showToast({
          type: 'success',
          body: 'Unterschrift gespeichert!',
        })
      );
    } catch (error) {
      console.error('Upload failed:', error);
      dispatch(
        showToast({
          type: 'error',
          body: 'Upload fehlgeschlagen. Bitte versuchen Sie es erneut.',
        })
      );
      setLocalDataUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle clear signature
  const handleClearSignature = () => {
    const pad = sigCanvasRef.current;
    if (pad) {
      try {
        pad.clear();
      } catch {}
    }
    setHasDrawn(false);
  };

  // Handle edit signature
  const handleEditSignature = () => {
    setIsEditing(true);
    setHasDrawn(false);
    // Clear the canvas for new signature
    const pad = sigCanvasRef.current;
    if (pad) {
      try {
        pad.clear();
      } catch {}
      if (typeof pad.on === 'function') {
        pad.on();
      }
    }
  };

  // Handle cancel editing - restore previous signature
  const handleCancelEdit = () => {
    setIsEditing(false);
    setHasDrawn(!!signature);
    // Clear the canvas since we're canceling
    const pad = sigCanvasRef.current;
    if (pad) {
      try {
        pad.clear();
      } catch {}
    }
  };

  // Handle drawing start
  const handleBegin = () => {
    setHasDrawn(true);
  };

  return (
    <div className={styles.signatureContainer}>
      <div className={styles.signatureHeader}>
        <p>Bitte unterschreiben</p>
        <div className={styles.actions}>
          {!isEditing && signature && (
            <img
              src={EditIcon}
              alt="Edit"
              onClick={handleEditSignature}
              className={styles.actionIcon}
            />
          )}
        </div>
      </div>

      <div
        className={cx(styles.signatureWrapper, {
          [styles.editing]: isEditing,
        })}
        ref={canvasWrapperRef}
      >
        {/* Show saved signature when not editing */}
        {signature && !isEditing && (
          <div className={styles.savedSignature}>
            {/* Show loading state while fetching signature */}
            {isLoadingSignature && (
              <div className={styles.loadingSignature}>
                <div className={styles.spinner} />
                <span>Laden...</span>
              </div>
            )}
            {/* Show local data URL while remote image loads to prevent white flash */}
            {!isLoadingSignature && localDataUrl && !imageLoaded && (
              <img src={localDataUrl} alt="Signature" className={styles.signaturePreview} />
            )}
            {!isLoadingSignature && signatureBlobUrl && (
              <img
                src={signatureBlobUrl}
                alt="Signature"
                onLoad={() => {
                  setImageLoaded(true);
                  setLocalDataUrl(null);
                }}
                style={{ opacity: imageLoaded || !localDataUrl ? 1 : 0, position: localDataUrl && !imageLoaded ? 'absolute' : 'relative' }}
              />
            )}
            {isUploading && (
              <div className={styles.uploadingOverlay}>
                <div className={styles.spinner} />
                <span>Wird hochgeladen...</span>
              </div>
            )}
          </div>
        )}

        {/* Show canvas when editing */}
        {(isEditing || !signature) && (
          <>
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="#7956D8"
              canvasProps={{
                className: styles.signatureCanvas,
              }}
              onBegin={handleBegin}
            />
            {isUploading && (
              <div className={styles.uploadingOverlay}>
                <div className={styles.spinner} />
                <span>Wird hochgeladen...</span>
              </div>
            )}
          </>
        )}
      </div>

      {isEditing && (
        <div className={styles.buttonRow}>
          {signature ? (
            // When editing existing signature, show Cancel and Save
            <>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleCancelEdit}
                disabled={isUploading}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className={cx(styles.saveBtn, { [styles.disabled]: !hasDrawn || isUploading })}
                onClick={handleSaveSignature}
                disabled={!hasDrawn || isUploading}
              >
                {isUploading ? 'Speichern...' : 'Speichern'}
              </button>
            </>
          ) : (
            // When creating new signature, show Clear and Save
            <>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleClearSignature}
                disabled={isUploading}
              >
                Entfernen
              </button>
              <button
                type="button"
                className={cx(styles.saveBtn, { [styles.disabled]: !hasDrawn || isUploading })}
                onClick={handleSaveSignature}
                disabled={!hasDrawn || isUploading}
              >
                {isUploading ? 'Speichern...' : 'Speichern'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizSignature;
